import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "./styles.css";

import CodeEditor from "../../../components/BuilderComponents/CodeEditor/CodeEditor";
import Button from "../../../components/FormComponents/Button";
import AppLeftSideBar from "../../../components/SideBar/AppLeftSideBar";
import axiosInstance, { handleApiError } from "../../../services/axiosService.js";
import BuilderModal from "../../../Layouts/Modal/BuilderModal.js";
import AddTheme from "../AddTheme.js";
import { toastSuccess } from "../../../utils/toastUtils.js";

import { index } from "./index.js";
import { rvTable } from "./rvTable.js";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon.js";
import Input from "../../../components/FormComponents/Input.js";

const ThemeEditor = ({ from = "auth" }) => {
  const [html, setHtml] = useState(index);
  const [css, setCss] = useState("");
  const [toAdd, setToAdd] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [newThemeName, setNewThemeName] = useState("");
  const navigate = useNavigate();

  const { themeId, orgId } = useParams();

  useEffect(() => {
    const getTheme = async () => {
      try {
        const { data } = await axiosInstance.get(`/sysconfigs/${themeId}`);
        if (data.config?.css) {
          setCss(data.config.css);
        }
        setSelectedTheme(data);
        setNewThemeName(data.name);
      } catch (error) {
        console.error("Error fetching theme:", error);
      }
    };

    getTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeId]);

  useEffect(() => {
    const handleClick = (event) => {
      const link = event.target.closest("a");
      if (link) {
        event.preventDefault();
        if (link.getAttribute("href") === "./index.html") {
          setHtml(index);
        } else if (link.getAttribute("href") === "./rvTable.html") {
          setHtml(rvTable);
        }
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const handleSaveTheme = async () => {
    try {
      const config = {
        config: {
          css: css,
          device: selectedTheme?.config?.device
        }
      };
      const { message } = await axiosInstance.put(`/screentheme/sysconfigs/${selectedTheme.id}`, {
        ...config
      });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleRenameTheme = async () => {
    try {
      const { message } = await axiosInstance.put(`/sysconfigs/${selectedTheme.id}`, {
        name: newThemeName
      });
      setSelectedTheme({ ...selectedTheme, name: newThemeName });
      toastSuccess(message || "Theme renamed successfully.");
      setIsEditingName(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const cancelRename = () => {
    setNewThemeName(selectedTheme.name);
    setIsEditingName(false);
  };

  const handleNavigate = () => {
    if (from === "auth") {
      navigate("/theme");
    } else if (from === "org") {
      navigate(`/organization/${orgId}/theme`);
    }
  };

  return (
    selectedTheme && (
      <div className="application_container">
        <AppLeftSideBar type={orgId ? "app" : "org"} />
        <div className="dashboard_outer_section" style={{ paddingBottom: "0px" }}>
          <div className="d_flex align_center gap_2 mb_4 mt_2">
            <div className="d_flex align_center cursor_pointer" onClick={handleNavigate}>
              <MaterialIcon name="arrow_back" />
            </div>
            <h2 style={{ margin: 0 }} className="d_flex align_center gap_1">
              <span>Theme Editor -</span>
              {isEditingName ? (
                <div className="d_flex align_center gap_2">
                  <Input type="text" value={newThemeName} onChange={(e) => setNewThemeName(e.target.value)} />
                  <MaterialIcon name="check" className="builder_action_btn" onClick={handleRenameTheme} />
                  <MaterialIcon name="close" className="builder_action_btn" onClick={cancelRename} />
                </div>
              ) : (
                <div className="d_flex align_center gap_1" style={{ marginLeft: "4px" }}>
                  <span>{selectedTheme.name}</span>
                  <MaterialIcon name="edit" className="builder_action_btn" onClick={() => setIsEditingName(true)} />
                </div>
              )}
            </h2>
          </div>
          <div className="theme-editor-page-outer">
            <div className="theme-editor-code-outer">
              <CodeEditor
                handleChange={setCss}
                height="calc(100% - 48px)"
                containerClass="theme-code-editor"
                language="css"
                value={css}
                enableBacktickTrigger={false}
              />
              <div className="d_flex gap_3 justify_end">
                <Button title="Save" className="mt_2" onClick={handleSaveTheme}>
                  Save
                </Button>
                <Button
                  title="Save"
                  className="mt_2"
                  onClick={() => {
                    setToAdd(true);
                  }}
                >
                  Save As
                </Button>
              </div>
            </div>

            <div className="theme-editor-preview-page-wrap">
              <div className="theme-editor-preview-page">
                <style>{css}</style>
                <div dangerouslySetInnerHTML={{ __html: html }} />
              </div>
            </div>
          </div>
        </div>
        <BuilderModal isOpen={toAdd} onClose={() => setToAdd(false)} modalName="Add Theme">
          <AddTheme
            setThemes={() => {}}
            setSelectedThemeConfig={() => {}}
            onClose={() => setToAdd(false)}
            deviceFilter={selectedTheme?.config?.device}
            css={css}
            from={from}
          />
        </BuilderModal>
      </div>
    )
  );
};

export default ThemeEditor;
